/**
 * Sets data-c2pa on Shopify CDN images that have a C2PA manifest (Timex index JSON).
 * Keeps attributes in sync for scanners; loaded when theme C2PA setting is enabled.
 */
;(() => {
  if (window.timexC2paStarted) return
  window.timexC2paStarted = true

  const indexUrl = 'https://assets.timex.com/c2pa/index-eu.json'

  const imageExtension = /\.(?:avif|gif|jpe?g|png|webp)$/i
  const uuidSuffix = /_[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i

  const sourceCache = new Map()
  const checkedImages = new WeakMap()
  const pendingImages = new Set()
  const pendingContainers = new Set()

  let manifests
  let batchScheduled = false

  function getManifestName(source) {
    if (sourceCache.has(source)) {
      return sourceCache.get(source)
    }

    let name = null

    try {
      if (
        source &&
        source.includes('/files/') &&
        !/^(data:|blob:)/i.test(source)
      ) {
        const url = new URL(source, window.location.href)

        const shopifyImage =
          url.hostname === window.location.hostname ||
          url.hostname === 'cdn.shopify.com' ||
          url.hostname.endsWith('.myshopify.com')

        if (
          /^https?:$/.test(url.protocol) &&
          shopifyImage &&
          url.pathname.includes('/files/')
        ) {
          const filename = decodeURIComponent(url.pathname.split('/').pop())

          if (imageExtension.test(filename)) {
            const base = filename
              .replace(imageExtension, '')
              .replace(uuidSuffix, '')

            if (base) {
              name = `${base}.c2pa`
            }
          }
        }
      }
    } catch {
      name = null
    }

    if (sourceCache.size >= 2000) {
      sourceCache.delete(sourceCache.keys().next().value)
    }

    sourceCache.set(source, name)
    return name
  }

  function updateImage(image) {
    if (!image.isConnected) return

    const source =
      image.currentSrc || image.getAttribute('src') || image.dataset.src || ''

    if (checkedImages.get(image) === source) return
    checkedImages.set(image, source)

    const manifestUrl = manifests.get(getManifestName(source))

    if (manifestUrl) {
      if (image.getAttribute('data-c2pa') !== manifestUrl) {
        image.setAttribute('data-c2pa', manifestUrl)
      }
    } else if (image.hasAttribute('data-c2pa')) {
      image.removeAttribute('data-c2pa')
    }
  }

  function processBatch() {
    batchScheduled = false

    pendingContainers.forEach((container) => {
      if (!container.isConnected) return

      let parent = container.parentNode

      while (parent && !pendingContainers.has(parent)) {
        parent = parent.parentNode
      }

      if (!parent) {
        container.querySelectorAll('img').forEach((image) => {
          pendingImages.add(image)
        })
      }
    })

    pendingContainers.clear()
    pendingImages.forEach(updateImage)
    pendingImages.clear()
  }

  function queueScan(root) {
    if (root instanceof HTMLImageElement) {
      pendingImages.add(root)
    } else if (root.querySelectorAll) {
      pendingContainers.add(root)
    }

    if (!batchScheduled && (pendingImages.size || pendingContainers.size)) {
      batchScheduled = true
      requestAnimationFrame(processBatch)
    }
  }

  async function start() {
    try {
      const response = await fetch(indexUrl, {
        credentials: 'omit',
      })

      if (!response.ok) return

      const index = await response.json()

      if (
        index.version !== 1 ||
        !index.files ||
        typeof index.files !== 'object' ||
        Array.isArray(index.files)
      ) {
        return
      }

      manifests = new Map()

      for (const [name, value] of Object.entries(index.files)) {
        if (
          !/^[A-Za-z0-9._-]+\.c2pa$/i.test(name) ||
          typeof value !== 'string'
        ) {
          continue
        }

        try {
          const url = new URL(value)

          if (
            url.protocol === 'https:' &&
            url.hostname === 'cdn.shopify.com' &&
            !url.username &&
            !url.password &&
            !url.port &&
            url.pathname.includes('/files/') &&
            decodeURIComponent(url.pathname.split('/').pop()) === name
          ) {
            manifests.set(name, url.href)
          }
        } catch {}
      }
    } catch {
      return
    }

    document.addEventListener(
      'load',
      (event) => {
        if (event.target instanceof HTMLImageElement) {
          queueScan(event.target)
        }
      },
      true
    )

    new MutationObserver((changes) => {
      changes.forEach((change) => {
        if (change.type === 'attributes') {
          queueScan(change.target)
        }

        change.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            queueScan(node)
          }
        })
      })
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset', 'data-src'],
    })

    queueScan(document)
  }

  function scheduleStart() {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(start, { timeout: 2000 })
    } else {
      setTimeout(start, 50)
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleStart, { once: true })
  } else {
    scheduleStart()
  }
})()
